import * as Sentry from '@sentry/nextjs'
import { Cause, Effect, Layer, pipe } from 'effect'

import {
  PublicEnvironmentConfig,
  type Runtime,
  SharedRuntimeConfig,
  SharedSentryConfig,
} from '@viziertronic/config/public'
import {
  CrashReporter,
  type CrashReporterShape,
  type ReportingUser,
  SentryReportingUser,
} from '@viziertronic/crash-reporter'

export type SentryCrashReporterConfig = {
  readonly runtime: Runtime
  readonly dsn: string
  readonly release: string
  readonly appEnvironment: string
}

class SentryCrashReporter implements CrashReporterShape {
  private readonly config: SentryCrashReporterConfig
  private user: SentryReportingUser | null = null

  constructor(config: SentryCrashReporterConfig) {
    this.config = config
  }

  private initSentry() {
    return Effect.sync(() => {
      const commonOptions = {
        environment: this.config.appEnvironment,
        dsn: this.config.dsn,
        ...(this.config.release && { release: this.config.release }),
        // Disable Sentry tracing - we use OpenTelemetry/Grafana for distributed tracing
        tracesSampleRate: 0,
        debug: false,
        skipOpenTelemetrySetup: true,
      } satisfies Partial<
        Sentry.NodeOptions & Sentry.BrowserOptions & Sentry.EdgeOptions
      >

      return Sentry.init({
        ...commonOptions,
        ...(this.config.runtime === 'browser'
          ? ({
              /** We cannot use config.runtime here otherwise it will import replayIntegration anyway */
              integrations: process.env.NEXT_RUNTIME
                ? []
                : [Sentry.replayIntegration()],
              replaysSessionSampleRate: 0.1,
              replaysOnErrorSampleRate: 1,
              sendDefaultPii: true,
              // Disable Sentry trace propagation - we use OpenTelemetry for this
              tracePropagationTargets: [],
            } satisfies Partial<Sentry.BrowserOptions>)
          : {}),
      })
    })
  }

  init() {
    if (!this.config.dsn) {
      return Effect.logWarning(
        'Sentry DSN not configured, crash reporting disabled',
      )
    }

    return pipe(
      Effect.logInfo('Initializing SentryCrashReporter'),
      Effect.andThen(() => this.initSentry()),
      Effect.tap(
        Effect.logInfo('SentryCrashReporter initalized successfully').pipe(
          Effect.annotateLogs({ release: this.config.release }),
        ),
      ),
      Effect.catch((error) =>
        Effect.logError(`Failed to initialize Sentry: ${error}`),
      ),
      Effect.annotateLogs({
        currentRuntime: this.config.runtime,
      }),
      Effect.withSpan('SentryCrashReporter.init'),
    )
  }

  setUser(user: ReportingUser) {
    if (!this.config.dsn) {
      return Effect.void
    }

    return Effect.sync(() => {
      this.user = SentryReportingUser.fromReporting(user)
      Sentry.setUser(this.user)
    })
  }

  private createExceptionFromCause<E = unknown>(
    cause: Cause.Cause<E>,
  ): E | unknown | string {
    const firstFail = cause.reasons.find(Cause.isFailReason)
    if (firstFail) return firstFail.error

    const firstDie = cause.reasons.find(Cause.isDieReason)
    if (firstDie) return firstDie.defect

    return Cause.pretty(cause)
  }

  report<E = unknown>(
    error: Error | Cause.Cause<E>,
    additionalInfo?: Record<string, unknown>,
  ) {
    if (!this.config.dsn) {
      return Effect.void
    }

    return Effect.sync(() => {
      const options: {
        extra?: Record<string, unknown>
        user?: SentryReportingUser
      } = {}
      if (additionalInfo) {
        options.extra = additionalInfo
      }
      if (this.user) {
        options.user = this.user
      }
      Sentry.captureException(
        Cause.isCause(error) ? this.createExceptionFromCause(error) : error,
        options,
      )
    })
  }
}

export const SentryCrashReporterLayer = Layer.effect(
  CrashReporter,
  Effect.gen(function* () {
    const { runtime } = yield* SharedRuntimeConfig
    const { dsn, release } = yield* SharedSentryConfig
    const { appEnvironment } = yield* PublicEnvironmentConfig
    return new SentryCrashReporter({
      runtime,
      dsn,
      release,
      appEnvironment,
    })
  }),
)
